import { Component } from 'react';
import { get, flowRight as compose } from 'lodash';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/client/react/hoc';
import {
  MUTATION_UPDATE_VIEW,
  MUTATION_UPDATE_SORT_BY,
  MUTATION_UPDATE_SEARCH,
  MUTATION_RESET_SEARCH,
  QUERY_CUSTOMER_CRITERIA
} from './gql';
import { ReactChildrenPropType } from '../../../../lib/commonPropTypes';

const updateToggle = (toggle) => {
  localStorage.setItem('customer.toggle', toggle);
};

class CustomerContainer extends Component {
  constructor() {
    super();

    this.getSetting = this.getSetting.bind(this);
    this.resetSearch = this.resetSearch.bind(this);
    this.updatePagination = this.updatePagination.bind(this);
    this.updateSearch = this.updateSearch.bind(this);
    this.updateSortBy = this.updateSortBy.bind(this);
    this.updateView = this.updateView.bind(this);
  }

  // Retrieve a ui setting from props or localStorage
  getSetting(setting, fallback) {
    const { [setting]: existing } = this.props;

    const storage = localStorage.getItem(`customer.${setting}`);

    return storage || existing || fallback;
  }

  updatePagination = (pagination) => {
    const existing = JSON.parse(this.getSetting('pagination', '{}'));

    localStorage.setItem('customer.pagination', JSON.stringify({ ...existing, ...pagination }));
  };

  updateView = (view) => {
    const { updateView } = this.props;

    localStorage.setItem('customer.view', view);

    return updateView({ variables: { view } });
  };

  updateSortBy = (sortBy) => {
    const { updateSortBy } = this.props;

    localStorage.setItem('customer.sortBy', sortBy);

    return updateSortBy({ variables: { sortBy } });
  };

  updateSearch = (search) => {
    const { updateSearch } = this.props;

    localStorage.setItem('customer.search', search);

    return updateSearch({ variables: { search } });
  };

  resetSearch = () => {
    const { resetSearch } = this.props;

    return resetSearch();
  };

  buildProps = () => ({
    pagination: JSON.parse(this.getSetting('pagination', '{}')),
    search: this.getSetting('search'),
    sortBy: this.getSetting('sortBy'),
    toggle: this.getSetting('toggle'),
    view: this.getSetting('view'),
    resetSearch: this.resetSearch,
    updatePagination: this.updatePagination,
    updateSearch: this.updateSearch,
    updateSortBy: this.updateSortBy,
    updateToggle,
    updateView: this.updateView
  });

  render() {
    const { children, ...otherProps } = this.props;

    return children(this.buildProps(otherProps));
  }
}

CustomerContainer.propTypes = {
  children: ReactChildrenPropType.isRequired,
  updateView: PropTypes.func.isRequired,
  updateSortBy: PropTypes.func.isRequired,
  updateSearch: PropTypes.func.isRequired,
  resetSearch: PropTypes.func.isRequired,
  pagination: PropTypes.string,
  search: PropTypes.string.isRequired,
  sortBy: PropTypes.string.isRequired,
  toggle: PropTypes.string,
  view: PropTypes.string.isRequired
};

CustomerContainer.defaultProps = {
  toggle: undefined,
  pagination: '{}'
};

const CustomerContainerWithMutations = compose(
  graphql(MUTATION_UPDATE_VIEW, { name: 'updateView' }),
  graphql(MUTATION_UPDATE_SORT_BY, { name: 'updateSortBy' }),
  graphql(MUTATION_UPDATE_SEARCH, { name: 'updateSearch' }),
  graphql(MUTATION_RESET_SEARCH, { name: 'resetSearch' }),
  graphql(QUERY_CUSTOMER_CRITERIA, {
    options: () => ({
      fetchPolicy: 'cache-first',
      ssr: false // Authentication only works client side
    }),
    props: ({ data }) => {
      const { customerCriteria } = data;

      return {
        pagination: get(customerCriteria, 'paginationCustomer', '{}'),
        search: get(customerCriteria, 'searchCustomer', ''),
        sortBy: get(customerCriteria, 'sortByCustomer', ''),
        toggle: get(customerCriteria, 'toggleCustomer', ''),
        view: get(customerCriteria, 'viewCustomer', '')
      };
    }
  })
)(CustomerContainer);

export default CustomerContainerWithMutations;
